// ./src/components/features/EventTickets/TicketSalesList/TicketSalesList.tsx
//
// Issued-tickets / door-sales list — table on desktop (>= md), cards below.
// Columns: ticket (code + holder + buyer), type, status, price, purchased, scan
// (check-in/out + who). A per-row actions menu covers scan, reassign, change type,
// refund and delete. Tap-safe, rights-gated, selection-ready, swipe on phones.

import React, { useCallback, useState } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
  ActionIcon,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { STATUSES } from "@sdflc/utils";

import {
  EventTicket,
  eventTicketHolderName,
  ticketStateLabel,
  ticketStateColor,
  ticketSaleChannelLabel,
  TICKET_STATE,
  TICKET_SCAN_ACTION,
} from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  TableDateTime,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  ResponsiveMenu,
} from "@/components/generic";
import type { ActionSheetItem } from "@/components/generic";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";

export interface TicketRowActions {
  onScan?: (t: EventTicket, action: string) => void;
  onMarkSold?: (t: EventTicket) => void;
  onReassign?: (t: EventTicket) => void;
  onChangeType?: (t: EventTicket) => void;
  onAssignBib?: (t: EventTicket) => void;
  onRefund?: (t: EventTicket) => void;
  onDelete?: (t: EventTicket) => void;
}

export interface TicketSalesListProps extends TicketRowActions {
  data: EventTicket[];
  loading?: boolean;
  /**
   * ⚠ REQUIRED — whether the FIRST read has come back. A skeleton belongs to that read only; a
   * refetch keeps the rows OR the empty state on screen and lets ContentLoadingOverlay say it is
   * reloading. `loading && items.length === 0` re-showed it on every refetch of an EMPTY list, so
   * an empty page read skeleton → "none" → skeleton → "none". Required so a caller cannot drop it.
   */
  isLoaded: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onAdd?: () => void;
  isRemoving?: (id?: string) => boolean;
  isActing?: (id?: string) => boolean;
  highlightedId?: string | null;
  selection?: RowSelection;
  selectable?: boolean;
  hasQueryNarrowing?: boolean;
  /** ROADMAP A4 — date labels by id, so a recurring event's tickets say which date they admit to. */
  dateLabels?: Record<string, string>;
}

const isDisabled = (t: EventTicket): boolean => t.status === STATUSES.DISABLED;
const isTerminal = (t: EventTicket): boolean =>
  t.ticketState === TICKET_STATE.REFUNDED || t.ticketState === TICKET_STATE.VOID;

/**
 * May this ticket be managed — reassigned, moved to another type, given a bib, refunded? ONE answer for the row's menu
 * and the page's `…/:id/<step>` stages (ROADMAP #115, #120): a typed URL, or Back after a refund, must not reach past the
 * button. Editable, not refunded / void — and not removed (a removed ticket still reads by id).
 */
export const canManageTicket = (t: EventTicket): boolean =>
  recordHasRight(t, "edit") && !isTerminal(t) && t.status !== STATUSES.REMOVED;

const money = (amount?: string | null, currency?: string | null): string | null => {
  if (amount == null || amount === "") {
    return null;
  }

  const num = Number(amount);

  if (!Number.isFinite(num)) {
    return null;
  }

  const value = num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return currency ? `${currency} ${value}` : value;
};

const formatDT = (iso?: string | null): string | null => {
  if (!iso) {
    return null;
  }

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return null;
  }

  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const HiddenBadge: React.FC<{ t: EventTicket }> = ({ t }) => {
  const { t: tr } = useTranslation(["common"]);

  if (!isDisabled(t)) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="gray">
      {tr("common:status.hidden", { defaultValue: "Hidden" })}
    </Badge>
  );
};

/** ROADMAP #188 — sold by a test run (the event is in test mode): hidden once the event is live. */
const TestBadge: React.FC = () => {
  const { t: tr } = useTranslation(["ticketSales", "common"]);

  return (
    <Badge size="sm" variant="outline" color="orange">
      {tr("ticketSales:testRecord", { defaultValue: "Test" })}
    </Badge>
  );
};

const StateBadge: React.FC<{ t: EventTicket }> = ({ t }) => {
  if (!t.ticketState && !t.isTest) {
    return null;
  }

  return (
    <Group gap={4} wrap="wrap">
      {t.ticketState && (
        <Badge size="sm" variant="light" color={ticketStateColor(t.ticketState)}>
          {ticketStateLabel(t.ticketState)}
        </Badge>
      )}
      {t.isTest && <TestBadge />}
    </Group>
  );
};

// ROADMAP A4 — the event's date labels by id, provided once by the list so every row can name the date
// its ticket admits to without threading a prop through each row component.
const NO_DATE_LABELS: Record<string, string> = {};
const TicketDateLabelsContext = React.createContext<Record<string, string>>(NO_DATE_LABELS);

// On the desktop table the date has its OWN column (see DateCell), so the row passes showDate={false} to
// keep it from appearing twice; the mobile card has no columns and keeps the inline date badge.
const TypeBadge: React.FC<{ t: EventTicket; showDate?: boolean }> = ({ t, showDate = true }) => {
  const dateLabels = React.useContext(TicketDateLabelsContext);
  const dateLabel = showDate && t.eventTimeSlotId ? dateLabels[t.eventTimeSlotId] ?? null : null;

  if (!t.ticketType?.name && !dateLabel) {
    return null;
  }

  return (
    <>
      {t.ticketType?.name && (
        <Badge size="sm" variant="light" color="indigo">
          {t.ticketType.name}
        </Badge>
      )}
      {dateLabel && (
        <Badge size="sm" variant="light" color="grape">
          {dateLabel}
        </Badge>
      )}
    </>
  );
};

// ROADMAP A4 — the date a ticket admits to, as its own desktop column (recurring events only; the header,
// rows and skeleton all gate it on the same showDateColumn so the fixed-width columns stay aligned).
const DateCell: React.FC<{ t: EventTicket }> = ({ t }) => {
  const dateLabels = React.useContext(TicketDateLabelsContext);
  const dateLabel = t.eventTimeSlotId ? dateLabels[t.eventTimeSlotId] ?? null : null;

  if (!dateLabel) {
    return <Text c="dimmed">—</Text>;
  }

  return <Text>{dateLabel}</Text>;
};

const TicketIcon: React.FC<{ t: EventTicket; size?: number }> = ({ t, size = 34 }) => (
  <ThemeIcon variant="light" color={ticketStateColor(t.ticketState)} radius="xl" size={size}>
    <AppIcon iconName="Ticket" size={Math.round(size * 0.55)} />
  </ThemeIcon>
);

const IdentityCell: React.FC<{ t: EventTicket; code: string }> = ({ t, code }) => (
  <Box style={{ minWidth: 0 }}>
    <Group gap={6} wrap="nowrap">
      {t.bibNumber && (
        <Badge size="sm" variant="filled" color="grape">
          {`BIB ${t.bibNumber}`}
        </Badge>
      )}
      <Text size="md" fw={600} lineClamp={1} ff="monospace">
        {code}
      </Text>
      {t.seatAssignment && (
        <Badge size="xs" variant="outline" color="grape">
          {t.seatAssignment}
        </Badge>
      )}
      <HiddenBadge t={t} />
    </Group>
    {t.holderName && (
      <Group gap={4} wrap="nowrap">
        <AppIcon iconName="User" size={12} />
        <Text size="xs" c="dimmed" lineClamp={1}>
          {t.holderName}
        </Text>
      </Group>
    )}
    {t.ticketOrder?.buyerName && t.ticketOrder.buyerName !== t.holderName && (
      <Group gap={4} wrap="nowrap">
        <AppIcon iconName="ShoppingCart" size={12} />
        <Text size="xs" c="dimmed" lineClamp={1}>
          {t.ticketOrder.buyerName}
        </Text>
      </Group>
    )}
  </Box>
);

const PriceCell: React.FC<{ t: EventTicket }> = ({ t }) => {
  const priceStr = money(t.price, t.currencyCode);

  return (
    <Box>
      <Text size="md" fw={600}>
        {priceStr ?? "—"}
      </Text>
      {t.saleChannel && (
        <Text size="xs" c="dimmed" lineClamp={1}>
          {ticketSaleChannelLabel(t.saleChannel)}
        </Text>
      )}
    </Box>
  );
};

// One scan event (in or out): a non-shrinking label badge + timestamp on the top
// line, the person who scanned it on its own dimmed line below. Separate lines so
// nothing clips — the whole thing is either shown in full or (unscanned) not at all.
const ScanEvent: React.FC<{ label: string; color: string; at: string; by?: string | null }> = ({ label, color, at, by }) => (
  <Box>
    <Group gap={6} wrap="nowrap">
      <Badge size="xs" variant="light" color={color} style={{ flexShrink: 0 }}>
        {label}
      </Badge>
      <Text size="xs" c="dimmed" lineClamp={1}>
        {at}
      </Text>
    </Group>
    {by ? (
      <Text size="xs" c="dimmed" lineClamp={1}>
        {by}
      </Text>
    ) : null}
  </Box>
);

const ScanCell: React.FC<{ t: EventTicket }> = ({ t }) => {
  const { t: tr } = useTranslation(["ticketSales"]);
  const inAt = formatDT(t.checkedInAt);
  const outAt = formatDT(t.checkedOutAt);

  if (!inAt && !outAt) {
    return (
      <Text size="xs" c="dimmed">
        {tr("ticketSales:scan.notScanned", { defaultValue: "Not scanned" })}
      </Text>
    );
  }

  // #169 — who, and where the scan was made (an entrance; for an add-on, where it was handed out).
  const by = (name?: string | null, where?: string | null): string | null =>
    [
      name ? tr("ticketSales:scan.by", { defaultValue: "by {{name}}", name }) : null,
      where ? tr("ticketSales:scan.at", { defaultValue: "at {{where}}", where }) : null,
    ]
      .filter(Boolean)
      .join(" · ") || null;

  return (
    <Stack gap={6}>
      {inAt && (
        <ScanEvent
          label={t.isAddOn ? tr("ticketSales:scan.given", { defaultValue: "Given" }) : tr("ticketSales:scan.in", { defaultValue: "In" })}
          color={t.isAddOn ? "grape" : "teal"}
          at={inAt}
          by={by(t.checkedInByLabel, t.checkedInServicePoint?.name)}
        />
      )}
      {outAt && (
        <ScanEvent label={tr("ticketSales:scan.out", { defaultValue: "Out" })} color="gray" at={outAt} by={by(t.checkedOutByLabel, t.checkedOutServicePoint?.name)} />
      )}
    </Stack>
  );
};

/** One scan step a ticket can take now. */
interface ScanChoice {
  key: string;
  icon: string;
  label: string;
  action: string;
}

/**
 * The scan steps a ticket can take now — ONE list for the ⋯ menu and the phone's swipe actions, so the two can never
 * offer different things.
 */
const scanChoicesFor = (t: EventTicket, actions: TicketRowActions, tr: TFunction): ScanChoice[] => {
  // Scanning only makes sense once the ticket is live (sold / assigned / already
  // checked in) — a blank "generated" or "reserved" ticket has no attendee yet.
  const scannable =
    t.ticketState === TICKET_STATE.SOLD || t.ticketState === TICKET_STATE.ASSIGNED || t.ticketState === TICKET_STATE.CHECKED_IN;

  if (!actions.onScan || !recordHasRight(t, "edit") || !scannable) {
    return [];
  }

  const checkedIn = !!t.checkedInAt;
  const checkedOut = !!t.checkedOutAt;
  const choices: ScanChoice[] = [];

  if (!checkedIn) {
    choices.push({ key: "ci", icon: "Login", label: tr("ticketSales:actions.checkIn", { defaultValue: "Check in" }), action: TICKET_SCAN_ACTION.CHECK_IN });
  } else if (!checkedOut) {
    choices.push({ key: "co", icon: "Logout", label: tr("ticketSales:actions.checkOut", { defaultValue: "Check out" }), action: TICKET_SCAN_ACTION.CHECK_OUT });
    choices.push({ key: "uci", icon: "X", label: tr("ticketSales:actions.undoCheckIn", { defaultValue: "Undo check-in" }), action: TICKET_SCAN_ACTION.UNDO_CHECK_IN });
  } else {
    // #228 — they went out: back in (a re-entry — the server says whether the event or the ticket type allows it). No
    // "Undo check-in" here: on somebody who left it would wipe the whole visit; undo the check-out first.
    choices.push({ key: "cia", icon: "Login", label: tr("ticketSales:actions.checkInAgain", { defaultValue: "Check in again" }), action: TICKET_SCAN_ACTION.CHECK_IN });
  }

  if (checkedOut) {
    choices.push({ key: "uco", icon: "X", label: tr("ticketSales:actions.undoCheckOut", { defaultValue: "Undo check-out" }), action: TICKET_SCAN_ACTION.UNDO_CHECK_OUT });
  }

  return choices;
};

/** One "manage" step the ⋯ menu offers for a ticket. */
interface ManageChoice {
  key: string;
  icon: string;
  label: string;
  run: (t: EventTicket) => void;
}

/** The menu's manage steps — ONE list, read by the ⋯ menu and by the phone's "More" swipe (offered only when non-empty). */
const manageChoicesFor = (t: EventTicket, actions: TicketRowActions, tr: TFunction): ManageChoice[] => {
  if (!recordHasRight(t, "edit")) {
    return [];
  }

  const choices: ManageChoice[] = [];

  if (actions.onMarkSold && t.ticketState === TICKET_STATE.GENERATED) {
    choices.push({ key: "ms", icon: "Coin", label: tr("ticketSales:actions.markSold", { defaultValue: "Mark as sold" }), run: actions.onMarkSold });
  }

  // ⚠ Every step below opens a URL stage that admits the ticket through this SAME function — never a copy of it.
  if (!canManageTicket(t)) {
    return choices;
  }

  if (actions.onReassign) {
    choices.push({ key: "ra", icon: "User", label: tr("ticketSales:actions.reassign", { defaultValue: "Reassign to guest" }), run: actions.onReassign });
  }

  if (actions.onChangeType) {
    choices.push({ key: "ct", icon: "Ticket", label: tr("ticketSales:actions.changeType", { defaultValue: "Change ticket type" }), run: actions.onChangeType });
  }

  if (actions.onAssignBib) {
    choices.push({ key: "bib", icon: "Hash", label: tr("bib:actions.assign", { defaultValue: "Assign BIB number" }), run: actions.onAssignBib });
  }

  if (actions.onRefund) {
    choices.push({ key: "rf", icon: "Coin", label: tr("ticketSales:actions.refund", { defaultValue: "Refund" }), run: actions.onRefund });
  }

  return choices;
};

const RowMenu: React.FC<{
  t: EventTicket;
  actions: TicketRowActions;
  isDeleting: boolean;
  /**
   * Phones: the menu is opened by the card's "More" SWIPE action, so there is no ⋯ button — the menu hangs off an
   * invisible anchor in the card's top-right corner. Absent = the usual ⋯ button (tablets, desktop).
   */
  anchored?: { opened: boolean; onClose: () => void };
}> = ({ t, actions, isDeleting, anchored }) => {
  const { t: tr } = useTranslation(["ticketSales", "common"]);
  const canRemove = recordHasRight(t, "remove");

  const scanItems: ActionSheetItem[] = scanChoicesFor(t, actions, tr).map((c) => ({ label: c.label, iconName: c.icon, onClick: () => actions.onScan?.(t, c.action) }));
  const manageItems: ActionSheetItem[] = manageChoicesFor(t, actions, tr).map((c) => ({ label: c.label, iconName: c.icon, onClick: () => c.run(t) }));
  const showDelete = actions.onDelete && canRemove;

  // A phone's sheet (ResponsiveMenu); on a phone it is opened by the card's "More" swipe action (`anchored`).
  return (
    <ResponsiveMenu
      title={t.ticketCode || tr("common:actions.more", { defaultValue: "More" })}
      position="bottom-end"
      width={220}
      {...(anchored ? { opened: anchored.opened, onOpenedChange: (o: boolean) => !o && anchored.onClose() } : {})}
      target={
        anchored ? (
          <Box aria-hidden style={{ position: "absolute", top: 8, right: 8, width: 1, height: 1 }} />
        ) : (
          <Tooltip label={tr("common:actions.more", { defaultValue: "More" })} withArrow position="bottom">
            <ActionIcon variant="subtle" color="gray" size="md" loading={isDeleting} disabled={isDeleting}>
              <AppIcon iconName="Dots" size={18} />
            </ActionIcon>
          </Tooltip>
        )
      }
      sections={[
        { items: scanItems },
        { items: manageItems },
        { items: showDelete ? [{ label: tr("common:buttons.delete", { defaultValue: "Delete" }), iconName: "Trash", color: "red", onClick: () => actions.onDelete?.(t) }] : [] },
      ]}
    />
  );
};

interface RowProps {
  t: EventTicket;
  code: string;
  disabled: boolean;
  actions: TicketRowActions;
  highlighted: boolean;
  isDeleting: boolean;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
  /** ROADMAP A4 — show the desktop Date column (recurring events only). The card view ignores it. */
  showDateColumn?: boolean;
}

const TicketTableRow: React.FC<RowProps> = ({
  t, code, disabled, actions, highlighted, isDeleting,
  selectionColumn, selectable, selected, onToggleSelect, showDateColumn,
}) => (
  <Table.Tr
    style={{
      opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
      backgroundColor: highlighted ? HIGHLIGHT_ROW_BG : selected ? "var(--mantine-color-blue-light)" : undefined,
      transition: "background-color 150ms ease, opacity 150ms ease",
    }}
  >
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        {selectable && (
          <Checkbox checked={selected} onChange={() => t.id && onToggleSelect?.(t.id)} disabled={isDeleting} aria-label={code} />
        )}
      </Table.Td>
    )}
    <Table.Td style={{ width: 150 }}>
      <TableDateTime value={t.soldAt || t.createdAt} />
    </Table.Td>
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <TicketIcon t={t} />
        <IdentityCell t={t} code={code} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 150 }}>
      <TypeBadge t={t} showDate={false} />
    </Table.Td>
    {showDateColumn && (
      <Table.Td style={{ width: 150 }}>
        <DateCell t={t} />
      </Table.Td>
    )}
    <Table.Td style={{ width: 120 }}>
      <StateBadge t={t} />
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <PriceCell t={t} />
    </Table.Td>
    <Table.Td style={{ width: 180 }}>
      <ScanCell t={t} />
    </Table.Td>
    <Table.Td style={{ width: 60 }}>
      <RowMenu t={t} actions={actions} isDeleting={isDeleting} />
    </Table.Td>
  </Table.Tr>
);

const TicketTableRowSkeleton: React.FC<{ selectionColumn?: boolean; showDateColumn?: boolean }> = ({ selectionColumn, showDateColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td style={{ width: 150 }}><Skeleton height={12} width={90} /></Table.Td>
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 150 }}><Skeleton height={18} width={70} radius="sm" /></Table.Td>
    {showDateColumn && <Table.Td style={{ width: 150 }}><Skeleton height={12} width={90} /></Table.Td>}
    <Table.Td style={{ width: 120 }}><Skeleton height={18} width={60} radius="sm" /></Table.Td>
    <Table.Td style={{ width: 140 }}><Skeleton height={14} width={80} /></Table.Td>
    <Table.Td style={{ width: 180 }}><Skeleton height={12} width={110} /></Table.Td>
    <Table.Td style={{ width: 60 }}><Skeleton circle height={28} width={28} /></Table.Td>
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const TicketCardComponent: React.FC<CardProps> = ({
  t, code, disabled, actions, highlighted, isDeleting,
  selectable, selected, selectionMode, onToggleSelect, forceClose, onForceCloseComplete, onSwipeStateChange,
}) => {
  const { t: tr } = useTranslation(["ticketSales", "common"]);
  const toggleSelect = () => t.id && onToggleSelect?.(t.id);
  // Phones: the full menu, opened by the "More" swipe action (there is no ⋯ button on a phone card).
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  // ⚠ 2026-10-07 (owner: swipeable actions are the house rule on phones) — the phone card showed the ⋯ button and its
  // swipe offered Delete alone. Now: the scan steps are "do something" swipes (swipe right — the door's quick path),
  // and the card's own side (swipe left) holds More (the whole menu, as on a tablet) and Delete.
  const swipeActions = React.useMemo((): SwipeAction[] => {
    const acts: SwipeAction[] = scanChoicesFor(t, actions, tr).map((c) => ({
      key: c.key,
      icon: c.icon === "X" ? "ArrowBackUp" : c.icon,
      label: c.label,
      color: c.icon === "X" ? "gray" : "teal",
      secondary: true,
      disabled: isDeleting,
      onClick: () => actions.onScan?.(t, c.action),
    }));

    if (manageChoicesFor(t, actions, tr).length > 0) {
      acts.push({
        key: "more",
        icon: "Dots",
        label: tr("common:actions.more", { defaultValue: "More" }),
        color: "gray",
        disabled: isDeleting,
        onClick: () => setMenuOpen(true),
      });
    }

    if (actions.onDelete && recordHasRight(t, "remove")) {
      acts.push({
        key: "delete",
        icon: "Trash",
        label: tr("common:buttons.delete", { defaultValue: "Delete" }),
        color: "red",
        onClick: () => actions.onDelete?.(t),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return acts;
  }, [actions, t, isDeleting, tr]);

  const cardBody = (showActions: boolean, showCheckbox: boolean, phoneMenu = false) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{
        position: "relative",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      {phoneMenu && <RowMenu t={t} actions={actions} isDeleting={isDeleting} anchored={{ opened: menuOpen, onClose: closeMenu }} />}
      <Group gap="md" wrap="nowrap" align="flex-start">
        {showCheckbox && (
          <Checkbox checked={selected} onChange={toggleSelect} onClick={(e) => e.stopPropagation()} disabled={isDeleting} aria-label={code} mt={4} />
        )}
        <TicketIcon t={t} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <IdentityCell t={t} code={code} />
          <Group gap={6} mt={6}>
            <TypeBadge t={t} />
            <StateBadge t={t} />
          </Group>
          <Group justify="space-between" mt={6} wrap="nowrap">
            <PriceCell t={t} />
            <TableDateTime inline value={t.soldAt || t.createdAt} />
          </Group>
          <Box mt={4}>
            <ScanCell t={t} />
          </Box>
        </Box>
        {showActions && <RowMenu t={t} actions={actions} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box hiddenFrom="sm" onClick={selectable ? toggleSelect : undefined} style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}>
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false, true)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const TicketCard = React.memo(TicketCardComponent);

// The card's shape: icon, then six rows — code, holder, the type + state badges, price + date, the sale channel under the
// price (every online order has one), the scan line — and the ⋯ on tablets only.
// ⓘ The tablet card's one button is the ⋯ menu (scan steps + manage); a phone swipes. (▶ check-card-skeletons)
// ⓘ Measured 167px against the card's 168 (ROADMAP #127). A buyer other than the holder adds an xs line, and each scan
// adds its "by …" line — both the minority (a third of tickets, and the few scanned), so neither is drawn.
const TicketCardSkeleton: React.FC<{ withCheckbox: boolean }> = ({ withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={6} sizes={['md', 'xs', 'xs', 'md', 'xs', 'xs']} gap={3} />
      <CardSkeletonActions count={1} />
    </Group>
  </Paper>
);

const TicketSalesListComponent: React.FC<TicketSalesListProps> = ({
  data,
  loading = false,
  isLoaded,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  onScan,
  onMarkSold,
  onReassign,
  onChangeType,
  onAssignBib,
  onRefund,
  onDelete,
  onAdd,
  isRemoving = () => false,
  isActing = () => false,
  highlightedId,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t } = useTranslation(["ticketSales", "common"]);
  const { isDesktop } = useAppEnvironment();

  // ROADMAP A4 — the Date column exists only when the event supplies date labels (recurring events). Read
  // from the context the wrapper provides so header, rows and skeleton all gate on the one value.
  const dateLabels = React.useContext(TicketDateLabelsContext);
  const showDateColumn = Object.keys(dateLabels).length > 0;

  const isSelectable = selectable && !!selection;
  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const wrap = useCallback(<T,>(fn?: (t: T) => void) => (fn ? (arg: T) => { closeAllCards(); fn(arg); } : undefined), [closeAllCards]);
  const actions: TicketRowActions = {
    onScan: onScan ? (ticket, action) => { closeAllCards(); onScan(ticket, action); } : undefined,
    onMarkSold: wrap(onMarkSold),
    onReassign: wrap(onReassign),
    onChangeType: wrap(onChangeType),
    // ⚠ Was never passed on (2026-10-07 review): the page handed it in and "Assign BIB number" never appeared.
    onAssignBib: wrap(onAssignBib),
    onRefund: wrap(onRefund),
    onDelete: wrap(onDelete),
  };
  const handleSwipeStateChange = useCallback((id: string, isOpen: boolean) => { if (isOpen) { openCard(id); } }, [openCard]);

  // ⚠ The FIRST read only — see `isLoaded` on the props. ▶ npm run check-list-states
  const showSkeleton = !isLoaded;

  if (!showSkeleton && data.length === 0) {
    return (
      <Paper withBorder p="xl" radius="md">
        {hasQueryNarrowing ? (
          <EmptyState
            title={t("ticketSales:emptyState.noMatchTitle", { defaultValue: "No tickets match" })}
            description={t("ticketSales:emptyState.noMatchDescription", { defaultValue: "Try a different search or clear the filters." })}
            iconName="Search"
            size="md"
          />
        ) : (
          <EmptyState
            title={t("ticketSales:emptyState.title", { defaultValue: "No tickets sold yet" })}
            description={t("ticketSales:emptyState.description", { defaultValue: "Sell a ticket at the door or generate tickets for in-person sale — they'll show up here." })}
            iconName="Ticket"
            actionLabel={t("ticketSales:emptyState.actionLabel", { defaultValue: "Sell at the door" })}
            onAction={onAdd}
            size="md"
          />
        )}
      </Paper>
    );
  }

  const rowIsSelectable = (ticket: EventTicket): boolean =>
    isSelectable && !!onDelete && recordHasRight(ticket, "remove");

  const rowProps = (ticket: EventTicket): RowProps => ({
    t: ticket,
    code: ticket.ticketCode || eventTicketHolderName(ticket) || "—",
    disabled: isDisabled(ticket),
    actions,
    highlighted: ticket.id === highlightedId,
    isDeleting: isRemoving(ticket.id) || isActing(ticket.id),
    showDateColumn,
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(ticket),
    selected: selection?.isSelected(ticket.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = data.filter((r) => rowIsSelectable(r)).map((r) => r.id).filter((id): id is string => !!id);
  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Box style={{ overflowX: "auto" }}>
            <Table
              verticalSpacing="sm"
              horizontalSpacing="md"
              miw={900}
              styles={{ th: { verticalAlign: "top" }, td: { verticalAlign: "top" } }}
            >
              <Table.Thead>
                <Table.Tr>
                  {isSelectable && (
                    <Table.Th style={{ width: 44 }}>
                      <Checkbox
                        checked={selection!.allSelected(selectableVisibleIds)}
                        indeterminate={selection!.someSelected(selectableVisibleIds)}
                        onChange={() => selection!.toggleAll(selectableVisibleIds)}
                        disabled={selectableVisibleIds.length === 0}
                        aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                      />
                    </Table.Th>
                  )}
                  <Table.Th style={{ width: 150 }}>{t("ticketSales:list.columns.purchased", { defaultValue: "Purchased" })}</Table.Th>
                  <Table.Th>{t("ticketSales:list.columns.ticket", { defaultValue: "Ticket" })}</Table.Th>
                  <Table.Th style={{ width: 150 }}>{t("ticketSales:list.columns.type", { defaultValue: "Type" })}</Table.Th>
                  {showDateColumn && (
                    <Table.Th style={{ width: 150 }}>{t("ticketSales:list.columns.date", { defaultValue: "Date" })}</Table.Th>
                  )}
                  <Table.Th style={{ width: 120 }}>{t("ticketSales:list.columns.status", { defaultValue: "Status" })}</Table.Th>
                  <Table.Th style={{ width: 140 }}>{t("ticketSales:list.columns.price", { defaultValue: "Price" })}</Table.Th>
                  <Table.Th style={{ width: 180 }}>{t("ticketSales:list.columns.scan", { defaultValue: "Check-in / out" })}</Table.Th>
                  <Table.Th style={{ width: 60 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {showSkeleton
                  ? Array.from({ length: 6 }).map((_, i) => <TicketTableRowSkeleton key={i} selectionColumn={isSelectable} showDateColumn={showDateColumn} />)
                  : data.map((ticket) => <TicketTableRow key={ticket.id} {...rowProps(ticket)} />)}
              </Table.Tbody>
            </Table>
          </Box>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 5 }).map((_, i) => <TicketCardSkeleton key={i} withCheckbox={isSelectable} />)}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap="sm">
        {data.map((ticket) => (
          <TicketCard
            key={ticket.id}
            {...rowProps(ticket)}
            forceClose={shouldForceClose(ticket.id || "")}
            onForceCloseComplete={() => handleForceCloseComplete(ticket.id || "")}
            onSwipeStateChange={(isOpen) => handleSwipeStateChange(ticket.id || "", isOpen)}
          />
        ))}
      </Stack>
      {loadMore}
    </Stack>
  );
};

const TicketSalesListWithDates: React.FC<TicketSalesListProps> = (props) => (
  <TicketDateLabelsContext.Provider value={props.dateLabels ?? NO_DATE_LABELS}>
    <TicketSalesListComponent {...props} />
  </TicketDateLabelsContext.Provider>
);

export const TicketSalesList = React.memo(TicketSalesListWithDates);
